<template>
  <AppModal :active="active" :header="true" :overlayClose="hasChanges()" :title="$t('modals.DocumentRoles.title')" @close="modalCloseDocumentRoles()">
    <div class="ModalDocumentRoles_container">
      <div class="ModalDocumentRoles_document">
      </div>
      <AppInputMultiSelect :editable="Account.permissions?.documents?.edit?.roles" v-model="selectedRoles" :options="availableRoles" :placeholder="$t('modals.DocumentRoles.inputs.roles.placeholder')" />
      <div class="buttons" v-if="active">
        <AppButton :loading="saveLoading" :disabled="hasChanges()" size="small" iconType="checkmark" @click="save()">{{ $t("modals.DocumentRoles.buttons.save") }}</AppButton>
      </div>
    </div>
  </AppModal>
</template>

<script>
import AppModal from "@/components/AppModal";
import { modalCloseDocumentRoles } from "@/modals/index";
import { useToast } from "vue-toastification";
import Organization from "@/core/organization";
import Document from "@/core/document";
import AppInputMultiSelect from "@/components/AppInputMultiSelect";
import AppButton from "@/components/AppButton";
import { checkErrors } from "@/utilities/errors";
import PrivilegesService from "@/services/privileges";
import events from "@/events";
import Account from "@/core/account";

export default {
  name: "ModalDocumentRoles",
  components: {
    AppInputMultiSelect,
    AppModal,
    AppButton
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    organization: {
      type: Organization
    },
    document: {
      type: Document
    }
  },
  setup() {
    const toast = useToast();

    return {
      modalCloseDocumentRoles,
      toast,
      events,
      Account
    };
  },
  data() {
    return {
      saveLoading: false,
      selectedRoles: {},
      availableRoles: []
    };
  },
  watch: {
    active: function() {
      if (this.active) {
        let newSelectedRoles = {};

        for (let role of this.organization.roles) {
          newSelectedRoles[role.id] = this.document.roles.some(r => r.id === role.id);
        }

        this.selectedRoles = newSelectedRoles;
        this.availableRoles = this.organization.roles.map(role => {
          return {
            label: role.name,
            value: role.id
          };
        });
      } else {
        this.selectedRoles = {};
        this.availableRoles = [];
      }
    }
  },
  methods: {
    save() {
      this.saveLoading = true;

      let toAdd = 0;
      let toRemove = 0;

      //First pass
      this.availableRoles.forEach(role => {
        let isSelected = this.selectedRoles[role.value];
        if (isSelected) {
          let hasRole = this.document.roles.some(r => r.id === role.value);
          if (!hasRole) {
            toAdd++;
          }
        } else {
          let hasRole = this.document.roles.some(r => r.id === role.value);
          if (hasRole) {
            toRemove++;
          }
        }
      });

      let toAddCount = 0;
      let toRemoveCount = 0;

      const changeComplete = () => {
        return toAddCount === toAdd && toRemoveCount === toRemove;
      };

      const Privileges = new PrivilegesService(process.env.VUE_APP_API_URL, process.env.VUE_APP_API_ID);

      //Second pass
      this.availableRoles.forEach(role => {
        let isSelected = this.selectedRoles[role.value];
        if (isSelected) {
          let hasRole = this.document.roles.some(r => r.id === role.value);
          if (!hasRole) {
            Privileges.addDocumentRole(this.document.id, role.value)
                .then(() => {
                  toAddCount++;
                  if (changeComplete()) {
                    this.toast.success(this.$t("modals.DocumentRoles.toasts.save.success"));

                    this.events.$emit("role:edit");

                    this.saveLoading = false;

                    modalCloseDocumentRoles();
                  }
                })
                .catch((error) => {
                  checkErrors(error);
                });
          }
        } else {
          let hasRole = this.document.roles.some(r => r.id === role.value);
          if (hasRole) {
            Privileges.documentDeleteRole(this.document.id, role.value)
                .then(() => {
                  toRemoveCount++;
                  if (changeComplete()) {
                    this.toast.success(this.$t("modals.DocumentRoles.toasts.save.success"));

                    this.events.$emit("role:edit");

                    this.saveLoading = false;

                    modalCloseDocumentRoles();
                  }
                })
                .catch((error) => {
                  checkErrors(error);
                });
          }
        }
      });
    },
    hasChanges() {
      return Object.keys(this.selectedRoles).every(key => {
        let isSelected = this.selectedRoles[key];
        if (isSelected) {
          return this.document.roles.some(r => r.id === key);
        } else {
          return !this.document.roles.some(r => r.id === key);
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.ModalDocumentRoles_container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .ModalDocumentRoles_title {
    line-height: 1.25rem;
    font-weight: 600;
    font-size: 1.125rem;
    color: #2d2d2d;
    margin-top: 1.0rem;
    margin-bottom: 1.0rem;
  }

  .ModalDocumentRoles_document {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    font-size: 14px;
    line-height: 1.5;
    color: #4a4a4a;
    margin-bottom: 20px;
  }

  .buttons {
    margin-top: 1.5rem;
    margin-left: auto;
  }
}
</style>
